import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import {
  createSaleDepositTransferNotification,
  getSaleDepositCurrent,
} from "../../services";
import {
  CreateTransferNotificationRequest,
  SaleDepositCalculated,
  SaleDepositResponse,
} from "../../types/sale-deposit.type";
import { formatNumber } from "../../utils";
import {
  CASH_IN_HAND_HINT,
  CASH_IN_HAND_LABEL,
  FIXED_EXPENSE_FIELDS,
  FixedExpenseField,
  formatCashInHand,
  parseNonNegativeMoney,
  toDraftAmount,
} from "./transfer.utils";
import AmountInput from "./AmountInput";
import NoteAdjustmentLines from "./NoteAdjustmentLines";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

const compact = `@media ${COMPACT_QUERY}`;

type MoneyField = FixedExpenseField | "deposit_collection_amount";

type MoneyDraft = Record<MoneyField, string>;

interface AdditionalExpenseDraft {
  id: string;
  description: string;
  amount: string;
}

/**
 * What the modal needs to notify a transfer amount for one delivery line / business day.
 *
 * Anchored on the CHECK-IN rather than on a sale report, so the action is available as soon
 * as the salesperson checks in and their store transactions arrive — checker reconciliation
 * is no longer a prerequisite.
 *
 * Deliberately carries NO money: the modal fetches it from `/sale-deposits/current` instead.
 * The dashboard row's cash figure is EX-VAT (the reporting basis), whereas remittance is
 * computed VAT-inclusive on what the customer actually paid, so reusing the row's numbers
 * would show a preview ~7% below the amount the server goes on to save.
 */
export interface TransferNotificationTarget {
  checkinUuid: string;
  route: string | null;
}

interface TransferNotificationModalProps {
  open: boolean;
  target: TransferNotificationTarget | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

// Blank rather than "0": every money field is optional (empty = 0), so a prefilled
// zero would only force the user to clear it before typing.
const INITIAL_MONEY: MoneyDraft = {
  fuel_expense_amount: "",
  deposit_collection_amount: "",
  transfer_fee_amount: "",
  toll_fee_amount: "",
  parking_fee_amount: "",
  other_expense_amount: "",
};

let additionalExpenseSequence = 0;
const nextAdditionalExpenseId = () =>
  `additional-expense-${Date.now()}-${additionalExpenseSequence++}`;

const backendMoney = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const toMoneyDraft = (row: SaleDepositResponse | null): MoneyDraft => {
  if (!row) return INITIAL_MONEY;
  return {
    fuel_expense_amount: toDraftAmount(row.fuel_expense_amount),
    deposit_collection_amount: toDraftAmount(row.deposit_collection_amount),
    transfer_fee_amount: toDraftAmount(row.transfer_fee_amount),
    toll_fee_amount: toDraftAmount(row.toll_fee_amount),
    parking_fee_amount: toDraftAmount(row.parking_fee_amount),
    other_expense_amount: toDraftAmount(row.other_expense_amount),
  };
};

const toAdditionalDrafts = (
  row: SaleDepositResponse | null,
): AdditionalExpenseDraft[] =>
  (row?.additional_expenses ?? []).map((expense) => ({
    id: nextAdditionalExpenseId(),
    description: expense.description,
    amount: toDraftAmount(expense.amount),
  }));

function ReadonlyAmount({
  label,
  value,
  hint,
  wholeBaht = false,
}: {
  label: string;
  value: number | null | undefined;
  hint?: string;
  /** Cash in hand is whole baht — satang on it would misstate what is in the box. */
  wholeBaht?: boolean;
}) {
  return (
    <TextField
      label={label}
      value={
        wholeBaht && value != null ? formatCashInHand(value) : formatNumber(value)
      }
      helperText={hint}
      size="small"
      fullWidth
      slotProps={{ input: { readOnly: true } }}
      sx={{ "& .MuiInputBase-root": { bgcolor: "background.default" } }}
    />
  );
}

export default function TransferNotificationModal({
  open,
  target,
  onClose,
  onSaved,
}: TransferNotificationModalProps) {
  const [money, setMoney] = useState<MoneyDraft>(INITIAL_MONEY);
  const [additionalExpenses, setAdditionalExpenses] = useState<
    AdditionalExpenseDraft[]
  >([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [calculated, setCalculated] = useState<SaleDepositCalculated | null>(
    null,
  );
  const [fetching, setFetching] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  const checkinUuid = target?.checkinUuid;

  useEffect(() => {
    if (!open || !checkinUuid) return;

    setErrors({});
    setCalculated(null);
    setLoadFailed(false);
    setFetching(true);

    // Guard against a stale response landing after the user switched rows.
    let active = true;
    getSaleDepositCurrent(checkinUuid)
      .then(({ data }) => {
        if (!active) return;
        const current = data.data;
        if (!current) {
          setLoadFailed(true);
          return;
        }
        setCalculated(current.calculated);
        // Re-notifying an existing (un-submitted) row: reload what was entered before so a
        // correction to one figure does not mean retyping all of them.
        setMoney(toMoneyDraft(current.notification));
        setAdditionalExpenses(toAdditionalDrafts(current.notification));
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setFetching(false);
      });

    return () => {
      active = false;
    };
  }, [open, checkinUuid]);

  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const updateMoney = (field: MoneyField, value: string) => {
    setMoney((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const addAdditionalExpense = () =>
    setAdditionalExpenses((prev) => [
      ...prev,
      { id: nextAdditionalExpenseId(), description: "", amount: "" },
    ]);

  const updateAdditionalExpense = (
    id: string,
    patch: Partial<Pick<AdditionalExpenseDraft, "description" | "amount">>,
  ) => {
    setAdditionalExpenses((prev) =>
      prev.map((expense) =>
        expense.id === id ? { ...expense, ...patch } : expense,
      ),
    );
    Object.keys(patch).forEach((field) => clearError(`${id}.${field}`));
  };

  const removeAdditionalExpense = (id: string) => {
    setAdditionalExpenses((prev) =>
      prev.filter((expense) => expense.id !== id),
    );
    setErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith(`${id}.`)),
      ),
    );
  };

  const preview = useMemo(() => {
    const parsedMoney = Object.fromEntries(
      Object.entries(money).map(([key, value]) => [
        key,
        parseNonNegativeMoney(value),
      ]),
    ) as Record<MoneyField, number | null>;
    const parsedAdditional = additionalExpenses.map((expense) =>
      parseNonNegativeMoney(expense.amount),
    );
    const totalCash = backendMoney(calculated?.cash_total_value);
    const transfer =
      totalCash == null
        ? null
        : Math.max(
            0,
            Math.min(backendMoney(calculated?.transfer_total_value) ?? 0, totalCash),
          );
    // The backend counts cash in hand bill by bill (whole baht) — take its figure, and only
    // fall back to the subtraction while an older backend is still deployed.
    const countedCash = backendMoney(calculated?.physical_cash_total_value);
    const physicalCash =
      countedCash ??
      (totalCash == null || transfer == null ? null : totalCash - transfer);

    const expenses = [
      parsedMoney.fuel_expense_amount,
      parsedMoney.transfer_fee_amount,
      parsedMoney.toll_fee_amount,
      parsedMoney.parking_fee_amount,
      parsedMoney.other_expense_amount,
      ...parsedAdditional,
    ];
    const allExpensesValid = expenses.every((amount) => amount != null);
    const totalExpense = allExpensesValid
      ? (expenses as number[]).reduce((sum, amount) => sum + amount, 0)
      : null;
    const depositCollection = parsedMoney.deposit_collection_amount;
    // แจ้งยอดโอน = (ยอดเงินสด + ฝากเก็บ) − ค่าใช้จ่ายรวม ± ใบลดหนี้/ใบเพิ่มหนี้เงินสด. ยอดเงินสด is
    // physical cash; เงินโอน is excluded from the remit base (not subtracted). Mirrors
    // SaleDepositEntity, which the server then adds the notes' net to: that net is signed (a CN
    // is negative), so it is added as it comes.
    const noteNetAmount = calculated?.credit_debit_note_adjustment?.net_amount ?? 0;
    const requiredTransfer =
      physicalCash != null && depositCollection != null && totalExpense != null
        ? physicalCash + depositCollection - totalExpense + noteNetAmount
        : null;

    return { totalCash, transfer, physicalCash, totalExpense, requiredTransfer };
  }, [money, additionalExpenses, calculated]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    (Object.keys(money) as MoneyField[]).forEach((field) => {
      if (parseNonNegativeMoney(money[field]) == null) {
        nextErrors[field] = "จำนวนเงินต้องเป็น 0 หรือมากกว่า";
      }
    });

    additionalExpenses.forEach((expense) => {
      if (!expense.description.trim()) {
        nextErrors[`${expense.id}.description`] = "กรุณาระบุรายการ";
      }
      if (parseNonNegativeMoney(expense.amount) == null) {
        nextErrors[`${expense.id}.amount`] =
          "จำนวนเงินต้องเป็น 0 หรือมากกว่า";
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!target || !calculated || !validate()) return;

    const request: CreateTransferNotificationRequest = {
      checkin_uuid: target.checkinUuid,
      fuel_expense_amount: parseNonNegativeMoney(
        money.fuel_expense_amount,
      ) as number,
      deposit_collection_amount: parseNonNegativeMoney(
        money.deposit_collection_amount,
      ) as number,
      transfer_fee_amount: parseNonNegativeMoney(
        money.transfer_fee_amount,
      ) as number,
      toll_fee_amount: parseNonNegativeMoney(money.toll_fee_amount) as number,
      parking_fee_amount: parseNonNegativeMoney(
        money.parking_fee_amount,
      ) as number,
      other_expense_amount: parseNonNegativeMoney(
        money.other_expense_amount,
      ) as number,
      additional_expenses: additionalExpenses.map((expense) => ({
        description: expense.description.trim(),
        amount: parseNonNegativeMoney(expense.amount) as number,
      })),
    };

    setLoading(true);
    try {
      await createSaleDepositTransferNotification(request);
      onClose();
      await onSaved();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            p: 0,
            maxHeight: "90vh",
            [compact]: { maxHeight: "94vh", m: 2 },
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        แจ้งยอดโอน{target?.route ? `: ${target.route}` : ""}
        <IconButton
          aria-label="ปิด"
          onClick={handleClose}
          disabled={loading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        {fetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
        <Stack gap={2}>
          {loadFailed && (
            <Alert severity="error">
              ไม่สามารถโหลดยอดขายของสายส่งนี้ได้ กรุณาปิดหน้าต่างแล้วลองใหม่อีกครั้ง
            </Alert>
          )}
          <Typography variant="subtitle2" fontWeight={700}>
            ยอดจากรายการขาย
          </Typography>
          <ReadonlyAmount
            label="ยอดเงินเชื่อ"
            value={calculated?.credit_total_value}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <ReadonlyAmount
              label={CASH_IN_HAND_LABEL}
              value={preview.physicalCash}
              hint={CASH_IN_HAND_HINT}
              wholeBaht
            />
            <ReadonlyAmount label="ยอดเงินโอน" value={preview.transfer} />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: (theme) => theme.palette.primary.main + "14",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              รวมเงินสด
            </Typography>
            <Typography variant="body2" fontWeight={700} color="primary">
              {formatNumber(preview.totalCash)} บาท
            </Typography>
          </Box>

          <Divider />

          <Typography variant="subtitle2" fontWeight={700}>
            ค่าใช้จ่าย
          </Typography>
          {FIXED_EXPENSE_FIELDS.map((field) => (
            <AmountInput
              key={field.key}
              label={field.label}
              value={money[field.key]}
              error={errors[field.key]}
              disabled={loading}
              onChange={(value) => updateMoney(field.key, value)}
            />
          ))}

          {additionalExpenses.map((expense, index) => (
            <Box
              key={expense.id}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr) auto",
                  sm: "minmax(0, 1fr) 180px auto",
                },
                gap: 1,
                alignItems: "start",
              }}
            >
              <TextField
                label={`รายการเพิ่มเติม ${index + 1}`}
                value={expense.description}
                onChange={(event) =>
                  updateAdditionalExpense(expense.id, {
                    description: event.target.value,
                  })
                }
                size="small"
                fullWidth
                disabled={loading}
                error={Boolean(errors[`${expense.id}.description`])}
                helperText={errors[`${expense.id}.description`]}
                sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
              />
              <AmountInput
                label="จำนวนเงิน"
                value={expense.amount}
                error={errors[`${expense.id}.amount`]}
                disabled={loading}
                onChange={(value) =>
                  updateAdditionalExpense(expense.id, { amount: value })
                }
              />
              <IconButton
                aria-label={`ลบรายการเพิ่มเติม ${index + 1}`}
                color="error"
                disabled={loading}
                onClick={() => removeAdditionalExpense(expense.id)}
                sx={{ mt: 0.25 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addAdditionalExpense}
            disabled={loading}
            sx={{ alignSelf: "flex-end" }}
          >
            เพิ่มรายการค่าใช้จ่าย
          </Button>

          <Divider />

          <AmountInput
            label="ยอดฝากเก็บ"
            value={money.deposit_collection_amount}
            error={errors.deposit_collection_amount}
            disabled={loading}
            onChange={(value) =>
              updateMoney("deposit_collection_amount", value)
            }
          />

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "background.default",
              display: "grid",
              gap: 1,
            }}
          >
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Typography variant="body2" fontWeight={600}>
                ค่าใช้จ่ายรวม
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatNumber(preview.totalExpense)} บาท
              </Typography>
            </Box>
            <NoteAdjustmentLines adjustment={calculated?.credit_debit_note_adjustment} />
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Typography variant="body2" fontWeight={600}>
                แจ้งยอดโอน
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary">
                {formatNumber(preview.requiredTransfer)} บาท
              </Typography>
            </Box>
          </Box>
        </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={handleClose} disabled={loading}>
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={loading}
          // Saving without the server's money snapshot would compute against nothing.
          disabled={fetching || loadFailed || !calculated}
        >
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
